import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {setAccountPassword} from '../store/slices/authThunks';
import {
  authEmailSelector,
  needsPasswordSetupSelector,
  setAccountPasswordApiSelector,
} from '../store/selectors/auth.selectors';
import AuthCard from './auth/AuthCard';
import ResetPasswordForm, {ResetPasswordValues} from './auth/ResetPasswordForm';
import {useToast} from './Toast';

/**
 * The first password for an account that was created by a Paddle checkout.
 *
 * Provisioning creates the auth user without one, so until this screen is done the only way into the
 * account is another emailed login link — which is why `ProtectedRoute` sends every app route here
 * while `password_set` is false, rather than leaving it as something to do later.
 *
 * The write goes through `POST /rest/v1/user/password` (not `supabase.auth.updateUser`) because the
 * flag lives on the CensorMe user row. Its response is the only trustworthy confirmation: the JWT
 * claim keeps saying false until the session refreshes.
 */
const SetPasswordContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {showToast} = useToast();

  const email = useAppSelector(authEmailSelector);
  const needsPassword = useAppSelector(needsPasswordSetupSelector);
  const api = useAppSelector(setAccountPasswordApiSelector);

  // Nothing to do here for an account that already has a password — changing one is `/account`,
  // which asks for the current password first.
  useEffect(() => {
    if (!needsPassword) {
      navigate('/account', {replace: true});
    }
  }, [needsPassword, navigate]);

  const handleSubmit = async (values: ResetPasswordValues) => {
    try {
      await dispatch(setAccountPassword(values.password)).unwrap();
      showToast('Password set', 'success');
      navigate('/welcome', {replace: true});
    } catch {
      // The thunk's message is in the slice; the card renders it.
    }
  };

  return (
    <AuthCard
      title="Choose your password"
      subtitle={email
        ? `Your account is ready. Set a password for ${email} and you're in.`
        : 'Your account is ready. Set a password and you\'re in.'}
      error={api.error}
    >
      <ResetPasswordForm
        submitting={api.status === 'pending'}
        passwordLabel="Password"
        confirmLabel="Confirm password"
        submitLabel="Set password and continue"
        submittingLabel="Saving…"
        onSubmit={handleSubmit}
      />

      <p className="mt-3 text-xs text-gray-600">
                From now on you sign in with your email and this password. Magic links are only used to recover
                an account.
      </p>
    </AuthCard>
  );
};

export default SetPasswordContainer;
